<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import {
  fetchShop, exchangeCash,
  type ExchangePackage, type ExchangeCustomTerms, type ExchangeResult,
} from '../game/shop-api';
import cashIcon from '../assets/omerta/icons/cash.webp';
import goldBarsIcon from '../assets/omerta/icons/gold-bars.webp';
import { toast } from '../game/toast';
import { confirmAction } from '../game/confirm';
import exchPocket from '../assets/omerta/shop/exch-pocket.webp';
import exchBriefcase from '../assets/omerta/shop/exch-briefcase.webp';
import exchDuffel from '../assets/omerta/shop/exch-duffel.webp';

/** `spotlight` pulses the pack cards on entry (header cash chip → "here's how
 *  you get cash") — see HeroScreen's guide + `.guide-glow` in app.css. */
const props = defineProps<{ spotlight?: boolean }>();
const emit = defineEmits<{ (e: 'updated'): void; (e: 'topup'): void }>();

const packages = ref<ExchangePackage[]>([]);
const custom = ref<ExchangeCustomTerms | null>(null);
const commissionPct = ref(4);
const balance = ref<{ soft: number; premium: number } | null>(null);
const loading = ref(true);
const error = ref('');
const busy = ref<string | null>(null);
const justLaundered = ref<number | null>(null); // cash credited by the last exchange

onMounted(async () => {
  try {
    const v = await fetchShop();
    packages.value = v.exchange.packages;
    custom.value = v.exchange.custom;
    commissionPct.value = v.exchange.commissionPct;
    balance.value = v.balance;
    customBars.value = Math.max(v.exchange.custom.minBars, 100);
  }
  catch (e) { error.value = (e as Error).message; }
  finally { loading.value = false; }
});

// ── Custom amount — live quote at the base rate ──
const customBars = ref(100);
const customGross = computed(() =>
  custom.value ? customBars.value * custom.value.cashPerBar : 0);
const customCut = computed(() => Math.max(1, Math.round(customGross.value * commissionPct.value / 100)));
const customCredited = computed(() => customGross.value - customCut.value);
const customValid = computed(() =>
  !!custom.value && Number.isInteger(customBars.value)
  && customBars.value >= custom.value.minBars && customBars.value <= custom.value.maxBars);
const customAffordable = computed(() =>
  balance.value != null && customBars.value <= balance.value.premium);

/** Per-tier dressing — same visual system as the Gold Reserve's packs. */
interface TierLook {
  art: string; color: string;
  ribbon?: string; ribbonIcon?: string;
  card?: 'popular' | 'whale';
  btn: 'grey' | 'gold' | 'green';
}
const TIERS: Record<string, TierLook> = {
  pocket:    { art: exchPocket,    color: '#c8a24c', btn: 'grey' },
  briefcase: { art: exchBriefcase, color: '#e3c878', btn: 'gold', ribbon: 'Popular', ribbonIcon: '★', card: 'popular' },
  duffel:    { art: exchDuffel,    color: '#6f9e54', btn: 'green', ribbon: 'Best rate', ribbonIcon: '♛', card: 'whale' },
};
function tier(p: ExchangePackage, idx: number): TierLook {
  return TIERS[p.id] ?? Object.values(TIERS)[Math.min(idx, 2)];
}

function fmt(n: number): string { return n.toLocaleString('en-US'); }
function affordable(p: ExchangePackage): boolean {
  return balance.value != null && p.bars <= balance.value.premium;
}

function settle(res: ExchangeResult) {
  balance.value = res.balance;
  justLaundered.value = res.credited;
  toast(`+${fmt(res.credited)} cash — laundered clean`, { icon: cashIcon });
  emit('updated');
}

/**
 * Nothing to launder. The buttons used to just sit there disabled reading
 * "Short on bars", which is a dead end — say it out loud and offer the way out
 * (the Gold Reserve is the only source of bars).
 */
async function offerTopup(barsWanted: number) {
  const have = balance.value?.premium ?? 0;
  const ok = await confirmAction({
    kicker: 'The Counting House',
    title: have === 0 ? 'You have no gold bars' : 'Not enough gold bars',
    icon: goldBarsIcon,
    rows: [
      { label: 'Bars needed', value: fmt(barsWanted), icon: goldBarsIcon, tone: 'bright' },
      { label: 'In your vault', value: fmt(have), icon: goldBarsIcon, tone: 'error' },
      { label: 'Short by', value: fmt(Math.max(0, barsWanted - have)), tone: 'error' },
    ],
    note: 'The house launders bars into cash — buy bars at the Gold Reserve first.',
    confirmLabel: 'Buy gold bars',
  });
  if (ok) emit('topup');
}

async function launder(p: ExchangePackage) {
  if (busy.value) return;
  if (!affordable(p)) return offerTopup(p.bars);
  const ok = await confirmAction({
    kicker: 'The Counting House',
    title: `Launder ${p.name}?`,
    icon: tier(p, 0).art,
    rows: [
      { label: 'Bars in', value: fmt(p.bars), icon: goldBarsIcon, tone: 'bright' },
      { label: `House cut ${commissionPct.value}%`, value: `−${fmt(p.commission)}`, tone: 'error' },
      { label: 'You get', value: fmt(p.credited), icon: cashIcon, tone: 'success' },
    ],
    confirmLabel: 'Launder',
  });
  if (!ok) return;
  busy.value = p.id; error.value = ''; justLaundered.value = null;
  try { settle(await exchangeCash({ packageId: p.id })); }
  catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

async function launderCustom() {
  if (busy.value || !customValid.value) return;
  if (!customAffordable.value) return offerTopup(customBars.value);
  const ok = await confirmAction({
    kicker: 'The Counting House',
    title: `Launder ${fmt(customBars.value)} bars?`,
    icon: goldBarsIcon,
    rows: [
      { label: 'Bars in', value: fmt(customBars.value), icon: goldBarsIcon, tone: 'bright' },
      { label: `House cut ${commissionPct.value}%`, value: `−${fmt(customCut.value)}`, tone: 'error' },
      { label: 'You get', value: fmt(customCredited.value), icon: cashIcon, tone: 'success' },
    ],
    confirmLabel: 'Launder',
  });
  if (!ok) return;
  busy.value = 'custom'; error.value = ''; justLaundered.value = null;
  try { settle(await exchangeCash({ bars: customBars.value })); }
  catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}
</script>

<template>
  <section>
    <!-- header: kicker + title, both balances on the right (Gold Reserve layout) -->
    <div class="flex flex-wrap items-end justify-between gap-4 pb-4 border-b border-border/70">
      <div>
        <div class="font-label uppercase tracking-[0.3em] text-[9px] text-text-dim">Laundering desk</div>
        <h1 class="flex items-center gap-3 font-heading font-black text-[28px] text-[#f0e6d0] tracking-[0.05em] leading-tight">
          <img :src="cashIcon" class="w-7 h-7 ch-gem" alt="" />
          The Counting House
        </h1>
      </div>
      <div v-if="balance" class="flex flex-wrap items-center gap-2">
        <div class="ch-balance">
          <img :src="goldBarsIcon" class="w-4 h-4" alt="" />
          <div>
            <div class="font-heading text-[8px] tracking-[0.2em] uppercase text-text-dim leading-none">Gold bars</div>
            <div class="font-heading font-black text-lg text-gold-bright leading-tight">{{ fmt(balance.premium) }}</div>
          </div>
        </div>
        <div class="ch-balance">
          <img :src="cashIcon" class="w-4 h-4" alt="" />
          <div>
            <div class="font-heading text-[8px] tracking-[0.2em] uppercase text-text-dim leading-none">Cash</div>
            <div class="font-heading font-black text-lg text-gold leading-tight">{{ fmt(balance.soft) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- mb-10: clear air between the blurb and the money cards (same treatment
         the Gold Reserve got in its whitespace pass) -->
    <p class="font-body italic text-sm text-text-dim mt-3 mb-10">
      Bars go in, walking-around money comes out — the house keeps {{ commissionPct }}% off the top.
      Bigger bags launder at a better rate.
    </p>

    <p v-if="loading" class="text-text-dim">Loading&hellip;</p>
    <p v-if="error" class="mb-3 text-sm text-error">{{ error }}</p>

    <template v-if="!loading">
      <!-- inline settle banner -->
      <div v-if="justLaundered !== null" class="ch-granted">
        <img :src="cashIcon" class="w-6 h-6 ch-gem" alt="" />
        <span><strong class="text-gold-bright">+{{ fmt(justLaundered) }}</strong> cash added to your roll.</span>
      </div>

      <!-- pack grid — the Gold Reserve card system, cash-side -->
      <div class="ch-grid">
        <div
          v-for="(p, idx) in packages" :key="p.id"
          class="ch-pack"
          :class="{ 'ch-popular': tier(p, idx).card === 'popular', 'ch-whale': tier(p, idx).card === 'whale', 'guide-glow': props.spotlight }"
          :style="{ '--ch-c': tier(p, idx).color }"
        >
          <div class="ch-wash" aria-hidden="true"></div>
          <div v-if="tier(p, idx).ribbon" class="ch-ribbon">
            <span class="ch-ribbon-ic">{{ tier(p, idx).ribbonIcon }}</span>{{ tier(p, idx).ribbon }}
          </div>
          <div class="ch-art"><img :src="tier(p, idx).art" :alt="p.name" /></div>
          <div class="ch-amount" :style="{ color: tier(p, idx).color, textShadow: `0 0 16px ${tier(p, idx).color}55` }">
            {{ fmt(p.credited) }}
          </div>
          <div class="ch-unit">Clean cash</div>
          <div class="ch-name">{{ p.name }}</div>
          <div class="ch-break">
            <span>Gross {{ fmt(p.gross) }}</span>
            <span class="text-error">&minus; house {{ fmt(p.commission) }}</span>
          </div>
          <div class="ch-cost">
            <img :src="goldBarsIcon" alt="" />
            {{ fmt(p.bars) }} bars
          </div>
          <button
            class="ch-buy" :class="`ch-buy--${tier(p, idx).btn}`"
            :disabled="busy !== null"
            :title="affordable(p) ? undefined : 'Not enough gold bars'"
            @click="launder(p)"
          >{{ busy === p.id ? '…' : affordable(p) ? 'Launder' : 'Short on bars' }}</button>
        </div>
      </div>

      <!-- custom amount — launder any number of bars at the base rate -->
      <div v-if="custom" class="ch-custom">
        <div class="ch-custom-head">
          <div class="font-heading text-lg text-gold">Name your own amount</div>
          <div class="font-body text-xs text-text-dim">
            Any amount from {{ fmt(custom.minBars) }} to {{ fmt(custom.maxBars) }} bars —
            base rate, the bundles above pay better per bar.
          </div>
        </div>
        <div class="ch-custom-body">
          <!-- the slider spans the FULL laundering range, not the player's
               balance: clamping max to `premium` made min === max at 0 bars, so
               the thumb could not move at all (and the number input, capped at
               maxBars, drifted out of sync with it). Affordability is the
               button's job — it opens the "no gold bars" popup instead. -->
          <input
            type="range" class="ch-slider"
            :min="custom.minBars" :max="custom.maxBars" step="10"
            v-model.number="customBars"
          />
          <div class="ch-custom-input">
            <img :src="goldBarsIcon" class="w-4 h-4" alt="" />
            <input
              type="number" class="ch-custom-num"
              :min="custom.minBars" :max="custom.maxBars" v-model.number="customBars"
            />
          </div>
          <div class="ch-quote">
            <span class="ch-quote-row">Gross <b>{{ fmt(customGross) }}</b></span>
            <span class="ch-quote-row text-error">&minus; house {{ commissionPct }}% <b>&minus;{{ fmt(customCut) }}</b></span>
            <span class="ch-quote-row ch-quote-net">+ credited <b>{{ fmt(customCredited) }}</b></span>
          </div>
          <button
            class="ch-buy ch-buy--gold ch-custom-buy"
            :disabled="busy !== null || !customValid"
            @click="launderCustom"
          >{{ busy === 'custom' ? '…' : customAffordable ? 'Launder custom' : 'Short on bars' }}</button>
        </div>
        <p v-if="!customValid" class="font-body text-[11px] text-error mt-1">
          Enter a whole number between {{ fmt(custom.minBars) }} and {{ fmt(custom.maxBars) }}.
        </p>
      </div>

      <!-- short on bars? the reserve is next door -->
      <div class="mt-6 flex items-center gap-3">
        <span class="font-body italic text-sm text-text-dim">Vault running thin?</span>
        <button class="btn-deco btn-deco--ghost btn-deco--sm" @click="emit('topup')">+ Buy gold bars</button>
      </div>
    </template>
  </section>
</template>

<style scoped>
.ch-gem { animation: ch-gem-pulse 2.6s ease-in-out infinite; }
@keyframes ch-gem-pulse {
  0%, 100% { filter: drop-shadow(0 0 2px rgba(227, 200, 120, 0.25)); transform: scale(1); }
  50% { filter: drop-shadow(0 0 9px rgba(227, 200, 120, 0.75)); transform: scale(1.06); }
}

.ch-balance {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 14px;
  background: rgba(200, 162, 76, 0.05);
  border: 1px solid rgba(200, 162, 76, 0.18);
}

.ch-granted {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  margin-bottom: 16px;
  padding: 10px 16px;
  border: 1px solid rgba(200, 162, 76, 0.35);
  border-left: 3px solid var(--color-gold);
  background: rgba(200, 162, 76, 0.07);
  font-family: var(--font-body, serif);
  font-size: 15px;
  color: var(--color-text);
}

.ch-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
@media (max-width: 900px) { .ch-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 460px) { .ch-grid { grid-template-columns: 1fr; } }

.ch-pack {
  position: relative;
  display: flex; flex-direction: column; align-items: center;
  min-height: 270px;
  padding: 16px 12px 12px;
  background: linear-gradient(160deg, #14101c, #0a0812);
  border: 1px solid var(--color-border);
  overflow: hidden;
  transition: transform 200ms ease, box-shadow 200ms ease;
}
.ch-pack:hover { transform: translateY(-4px); box-shadow: 0 8px 28px rgba(0, 0, 0, 0.8); }
.ch-popular { border-color: rgba(227, 200, 120, 0.35); box-shadow: 0 0 16px rgba(227, 200, 120, 0.06); }
.ch-popular:hover { box-shadow: 0 12px 40px rgba(227, 200, 120, 0.14); }
.ch-whale { border-color: rgba(111, 158, 84, 0.45); box-shadow: 0 0 14px rgba(111, 158, 84, 0.1); }
.ch-whale:hover { box-shadow: 0 12px 40px rgba(111, 158, 84, 0.18); }

.ch-wash {
  position: absolute; inset: 0;
  pointer-events: none;
  opacity: 0.07;
  background: radial-gradient(ellipse at 50% 0%, var(--ch-c), transparent 70%);
}
.ch-ribbon {
  position: absolute; top: -1px; left: -1px;
  z-index: 2;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px;
  font-family: var(--font-heading, serif);
  font-size: 8px; font-weight: 700;
  letter-spacing: 0.2em; text-transform: uppercase;
  background: var(--color-gold);
  color: #06040e;
  white-space: nowrap;
}
.ch-ribbon-ic { font-size: 9px; line-height: 1; }

.ch-art {
  width: 96px; height: 96px;
  margin-top: 12px;
  position: relative; z-index: 1;
  filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.6));
}
.ch-art img { width: 100%; height: 100%; object-fit: contain; }

.ch-amount {
  position: relative; z-index: 1;
  margin-top: 12px;
  font-family: var(--font-heading, serif);
  font-size: 27px; font-weight: 700; line-height: 1;
  font-variant-numeric: tabular-nums;
}
.ch-unit {
  position: relative; z-index: 1;
  margin-top: 3px;
  font-family: var(--font-heading, serif);
  font-size: 9px; letter-spacing: 0.28em; text-transform: uppercase;
  color: #8a7a60;
}
.ch-name {
  position: relative; z-index: 1;
  margin-top: 6px;
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--color-gold);
}
.ch-break {
  position: relative; z-index: 1;
  display: flex; gap: 10px;
  margin-top: 4px;
  font-family: var(--font-body, serif);
  font-size: 11px;
  color: var(--color-text-dim);
  font-variant-numeric: tabular-nums;
}
.ch-cost {
  position: relative; z-index: 1;
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 8px; margin-bottom: 12px;
  font-family: var(--font-heading, serif);
  font-size: 15px; font-weight: 900;
  color: #fff;
  font-variant-numeric: tabular-nums;
}
.ch-cost img { width: 16px; height: 16px; object-fit: contain; }

.ch-buy {
  position: relative; z-index: 1;
  margin-top: auto;
  width: 100%;
  padding: 8px 0;
  font-family: var(--font-heading, serif);
  font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
  border: 1px solid;
  clip-path: polygon(4px 0, calc(100% - 4px) 0, 100% 4px, 100% calc(100% - 4px),
                     calc(100% - 4px) 100%, 4px 100%, 0 calc(100% - 4px), 0 4px);
  cursor: pointer;
  transition: filter 150ms ease, background 150ms ease;
}
.ch-buy:hover:not(:disabled) { filter: brightness(1.25); }
.ch-buy:active:not(:disabled) { transform: translateY(1px); }
.ch-buy:disabled { opacity: 0.55; cursor: not-allowed; }
.ch-buy--grey  { background: linear-gradient(180deg, #1a1622, #100d16); border-color: #2f2a3c; color: #9a8f7a; }
.ch-buy--gold  { background: linear-gradient(180deg, #2a1f08, #18120a); border-color: rgba(201, 145, 58, 0.5); color: #e3c878; }
.ch-buy--green { background: linear-gradient(180deg, #16220f, #0d1409); border-color: rgba(111, 158, 84, 0.55); color: #9ec98a; }

/* ── custom amount panel ── */
.ch-custom {
  margin-top: 20px;
  padding: 16px 18px;
  border: 1px solid rgba(200, 162, 76, 0.28);
  background: radial-gradient(120% 100% at 50% 0%, rgba(200, 162, 76, 0.06), transparent 70%), rgba(0, 0, 0, 0.18);
}
.ch-custom-head { margin-bottom: 12px; }
.ch-custom-body { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.ch-slider { flex: 1 1 240px; min-width: 180px; accent-color: var(--color-gold); cursor: pointer; }
.ch-custom-input {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 6px 10px; border: 1px solid var(--color-border); background: rgba(0, 0, 0, 0.3);
}
.ch-custom-num {
  width: 84px; background: transparent; border: 0; outline: none;
  font-family: var(--font-heading, serif); font-size: 15px; color: var(--color-gold-bright);
  font-variant-numeric: tabular-nums;
}
.ch-custom-num::-webkit-outer-spin-button, .ch-custom-num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.ch-quote {
  display: flex; flex-direction: column; gap: 2px;
  font-family: var(--font-body, serif); font-size: 12px; color: var(--color-text-dim);
  font-variant-numeric: tabular-nums;
  min-width: 150px;
}
.ch-quote-row { display: flex; justify-content: space-between; gap: 12px; }
.ch-quote-row b { font-family: var(--font-heading, serif); font-weight: 700; }
.ch-quote-net { color: var(--color-success, #6f9e54); }
.ch-custom-buy { width: auto; min-width: 150px; margin-top: 0; padding: 9px 18px; }

@media (prefers-reduced-motion: reduce) {
  .ch-gem { animation: none; }
  .ch-pack, .ch-pack:hover { transition: none; transform: none; }
}
</style>
